import { Locales } from '@griply/types';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { getGlobalAllowedSearchParameters } from 'shared/lib/react-router-dom';
import { getErrorReporter } from 'shared/services/error-tracking';
import { AuthFlowError } from 'shared/types/auth-flow-error';
import { ResetPasswordErrorTypes } from 'shared/types/reset-password-form';
import { User } from 'shared/types/user';

import { claimSignup } from './claim-signup';
import { createPushToken } from './create-push-token';
import { finishUserSignup } from './finish-user-signup';
import { getPushToken } from './get-push-token';
import { getUser } from './get-user';
import { getAuthentication } from './helpers';

export const completeSignupAndLogin = async (
  claimToken: string,
  password: string,
  { name, locale }: { name?: string; locale?: Locales },
) => {
  const auth = await getAuthentication();

  try {
    const { email } = await claimSignup(claimToken, password);
    await signInWithEmailAndPassword(auth, email, password);

    const notificationsEnabled =
      'Notification' in window && Notification.permission === 'granted';

    await finishUserSignup({
      uid: auth.currentUser?.uid,
      name,
      locale,
      hasNotifications: notificationsEnabled,
      queryParameters: Object.fromEntries(getGlobalAllowedSearchParameters()),
    });

    // Signup already completed; a failed profile read must not surface as a signup failure.
    let user: User | null = null;

    try {
      user = await getUser();

      if (user && notificationsEnabled) {
        const token = await getPushToken(user.uid);
        token && (await createPushToken(user.uid, token, locale));
      }
    } catch (error) {
      getErrorReporter().captureException(error, {
        tags: { source: 'complete-signup-post-auth-user-read' },
      });
    }

    return user;
  } catch (e) {
    getErrorReporter().captureException(e, {
      tags: { source: 'complete-signup-and-login' },
    });
    throw new AuthFlowError(ResetPasswordErrorTypes.Unknown, { cause: e });
  }
};
